/**
 * @module close-cycle
 * @category Internal
 *
 * Pure orchestration of the close-the-books flow: scan stream heads,
 * partition by reaction safety, guard with tombstones, optionally seed
 * restart state, run user archive callbacks, atomically truncate, and
 * update the cache.
 *
 * The Act orchestrator owns lifecycle (correlate gate, emit("closed")) and
 * the registry-derived inputs (reactive-event count, event→state map). All
 * sequential phase work between those state touches lives here.
 *
 * @internal
 */

import { cache, SNAP_EVENT, store, TOMBSTONE_EVENT } from "../ports.js";
import type {
  CloseResult,
  CloseTarget,
  Logger,
  Schema,
  State,
} from "../types/index.js";
import type { EsOps } from "./event-sourcing.js";

/**
 * Dependencies the close cycle needs from the Act orchestrator. Decoupled
 * from `Act` itself so the cycle can be exercised from tests in isolation.
 *
 * @internal
 */
export type CloseCycleDeps = {
  readonly reactive_events_size: number;
  readonly event_to_state: ReadonlyMap<string, State<any, any, any>>;
  readonly load: EsOps["load"];
  readonly tombstone: EsOps["tombstone"];
  readonly logger: Logger;
  /**
   * Correlation id for the close transaction. Caller (`Act.close`)
   * computes this via the configured {@link Correlator}, so close
   * commits share the user's chosen id scheme instead of stamping a
   * UUID.
   */
  readonly correlation: string;
  /**
   * Page size for the safety probe's `query_streams` pagination.
   * Defaults to {@link SAFETY_PROBE_PAGE_SIZE}; production callers omit
   * it, tests set a small value to exercise the multi-page path.
   */
  readonly probe_page_size?: number;
  /**
   * Advance correlation to at least `until` (an event id) and return how
   * far it actually got (#1487).
   *
   * The safety probe asks each subscription whether it has unconsumed
   * work, which is only a fair question about events correlate has already
   * resolved. An event past the read cursor has raised no mark yet, so
   * every reader answers "caught up" about it — including a reader that
   * does not exist yet, because the subscription a dynamic resolver would
   * create is itself a product of correlating that event.
   *
   * Refusing to close is the wrong answer: the autoclose path fires *from*
   * the event that reaches the terminal state, so its own trigger is
   * routinely uncorrelated, and a retired stream gets no further commits to
   * retry with. So the cycle makes the precondition true instead — it
   * correlates the tail, then decides. Whatever remains above the cursor
   * afterwards is held back as pending.
   */
  readonly catch_up_correlation: (until: number) => Promise<number>;
  /**
   * Per-stream critical section (#1222). The windowed branch is
   * deliberately guard-free at the store level — a past cutoff makes the
   * boundary immutable, so a concurrent append can never race the prune.
   * But that assumes a *single* closer per stream. A manual
   * `app.close([{stream, before}])` runs `run_close_cycle` directly,
   * bypassing the `__autoclose__:X` lease that would otherwise exclude a
   * concurrent autoclose windowed close, so both closers can archive the
   * same prefix — a double S3 upload / double JSONL append. This runs the
   * given work under a process-local per-stream lock so the two closers
   * serialize; the second sees the already-pruned prefix and skips its
   * archive. Provided by the Act orchestrator (shared across `app.close`
   * and the drain's `on_close`); defaults to identity (no serialization)
   * when the cycle is exercised in isolation.
   */
  readonly with_stream_lock?: <T>(
    stream: string,
    work: () => Promise<T>
  ) => Promise<T>;
};

/**
 * Page size for the safety probe's keyset pagination over the
 * subscriptions table. Above `query_streams`'s default `limit` of 100
 * to keep the round-trip count low while bounding per-page work.
 *
 * @internal
 */
export const SAFETY_PROBE_PAGE_SIZE = 1000;

/**
 * Per-stream scan result: latest non-tombstone domain event metadata.
 * `last_event_name` is always defined — the scan filters tombstones in the
 * callback and queries without `with_snaps`, so any event reaching the
 * callback is a domain event whose name we capture alongside id/version.
 */
type StreamHead = {
  readonly max_id: number;
  readonly version: number;
  readonly last_event_name: string;
  /**
   * Set when the stream already carries a tombstone but still holds domain
   * events — a close that wrote its guard and was then interrupted before
   * truncating (a throwing archive callback, or a `truncate` that failed).
   * Carries the existing guard's event id so the retry resumes at Phase 4
   * instead of re-tombstoning (which would fail the version guard) or being
   * dropped from the scan entirely (#1389).
   */
  readonly resumed_guard?: { readonly id: number };
};

/**
 * Run the full close cycle for the given targets. Caller owns the
 * lifecycle event emission.
 *
 * Targets carrying a `before` cutoff take the **windowed** branch — a
 * pure prefix delete behind an existing snapshot (see
 * {@link run_windowed_closes}); the rest run the guarded
 * tombstone/restart pipeline below.
 *
 * @internal
 */
export async function run_close_cycle(
  targets: CloseTarget[],
  deps: CloseCycleDeps
): Promise<CloseResult> {
  // Caller (Act.close) filters empty targets; run_close_cycle assumes at
  // least one target.
  const target_map = new Map(targets.map((t) => [t.stream, t]));
  for (const t of target_map.values()) {
    if (t.before !== undefined && t.restart)
      throw new Error(
        `close: \`before\` and \`restart\` are mutually exclusive (stream "${t.stream}") — a windowed close keeps the stream live behind a real snapshot; restart reseeds it`
      );
  }
  const windowed = [...target_map.values()].filter(
    (t) => t.before !== undefined
  );
  const full = [...target_map.values()].filter((t) => t.before === undefined);
  const skipped: string[] = [];
  const windowed_result = windowed.length
    ? await run_windowed_closes(windowed, deps, skipped)
    : new Map();
  if (!full.length) return { truncated: windowed_result, skipped };
  const streams = full.map((t) => t.stream);

  // 1. Scan: find the latest non-tombstone event per stream
  const stream_info = await scan_stream_heads(streams);

  // 1b. Reject restart targets whose owning state carries sensitive
  // fields, BEFORE anything is written. The seed load in phase 4 is
  // actorless, so every `sensitive()` field would fold to the redaction
  // sentinel and the truncate would then delete the originals — and
  // loading privileged instead would persist plaintext into
  // `__snapshot__.data`, which `forget_pii` cannot reach (the reason
  // `.snap()` is rejected at build time for these states). Such a stream
  // cannot be restarted at all, so it must not be closed either: the
  // caller asked to keep the aggregate alive, and tombstoning it instead
  // would be a strictly more destructive outcome than the one requested.
  // It lands in `skipped`, the established channel for "couldn't do this
  // one", with nothing mutated.
  for (const target of full) {
    if (!target.restart) continue;
    const info = stream_info.get(target.stream);
    if (!info) continue;
    const owner = deps.event_to_state.get(info.last_event_name);
    if (owner?.pii_aware) {
      deps.logger.error(
        `Refusing to close "${target.stream}" with restart: state "${owner.name}" carries sensitive fields, so a restart seed would persist redacted values while deleting the originals. Close it without restart to retire the stream, or leave it open.`
      );
      skipped.push(target.stream);
      stream_info.delete(target.stream);
    }
  }

  // 2. Partition: skip streams with pending reactions in flight
  const safe = await partition_by_safety(
    stream_info,
    deps.reactive_events_size,
    skipped,
    deps.probe_page_size ?? SAFETY_PROBE_PAGE_SIZE,
    deps.catch_up_correlation
  );
  if (!safe.length) return { truncated: windowed_result, skipped };

  // 3. Guard: commit a tombstone with expectedVersion per safe stream.
  // Correlation comes from the orchestrator's configured correlator so
  // close commits share the app's id scheme — see ACT-404.
  const { guarded, guard_events } = await guard_with_tombstones(
    safe,
    stream_info,
    deps.correlation,
    deps.tombstone,
    skipped
  );
  if (!guarded.length) return { truncated: windowed_result, skipped };

  // 4. Seed: load final state for restart targets through the owning state
  const seed_states = await load_restart_seeds(
    guarded,
    target_map,
    stream_info,
    deps.event_to_state,
    deps.load,
    deps.logger
  );

  // 5. Archive: user-provided per-stream callback while guarded
  await run_archive_callbacks(guarded, target_map);

  // 6. Truncate + seed: atomic per-store transaction
  const truncated = await truncate_and_warm_cache(
    guarded,
    seed_states,
    guard_events,
    deps.correlation
  );

  for (const [stream, entry] of windowed_result) truncated.set(stream, entry);
  return { truncated, skipped };
}

// ---------------------------------------------------------------------------
// Windowed branch — prune the prefix behind an existing snapshot
// ---------------------------------------------------------------------------

/**
 * Run the windowed closes: probe the min consumer watermark per stream
 * (the `max_id` cap that keeps the boundary at/below what the laggiest
 * consumer has read), run archive callbacks against the cutoff, then
 * hand the boundary targets to {@link Store.truncate}.
 *
 * No tombstone guard and no cache touch — the cutoff is always in the
 * past, so a concurrently-written snapshot (`created = now`) can never
 * become the boundary: once the cutoff is fixed the boundary snapshot is
 * fixed, and the prefix below it is immutable. Concurrent appends land
 * at the head, above the boundary. Current state is unchanged, so the
 * cache stays warm. Streams the store skips (no qualifying snapshot)
 * are reported in `skipped`.
 *
 * @internal
 */
async function run_windowed_closes(
  windowed: CloseTarget[],
  deps: CloseCycleDeps,
  skipped: string[]
): Promise<CloseResult["truncated"]> {
  // 1. Safety probe: min consumer watermark per stream. Skipped entirely
  // when the app has no reactions — nothing can lag.
  //
  // The cap asks each consumer "how far is it safe to prune?", and a
  // watermark alone stopped answering that when correlate became the producer
  // of the work mark (#1520). A subscription advances only over events that
  // resolve to it, so a reaction covering a subset of a state's events sits
  // permanently below the head with nothing pending — and capped the prune at
  // its frozen watermark, which for a retention window means pruning almost
  // nothing, every time, with no error and no `skipped` entry to explain it.
  //
  // A caught-up consumer is instead capped at the correlate checkpoint. Not
  // at infinity: events above the checkpoint have not been resolved yet, so a
  // mark for them may still be coming, and pruning past it could delete work
  // a consumer is about to be told about. Catching up first makes that bound
  // as generous as it can honestly be.
  const checkpoint =
    deps.reactive_events_size > 0
      ? await deps.catch_up_correlation(Number.MAX_SAFE_INTEGER)
      : -1;
  const min_at =
    deps.reactive_events_size > 0
      ? await probe_min_watermarks(
          windowed.map((t) => t.stream),
          deps.probe_page_size ?? SAFETY_PROBE_PAGE_SIZE,
          checkpoint
        )
      : new Map<string, number>();

  // 2 + 3. Per stream, under a process-local lock (#1222): probe the
  // boundary, archive against intact history only when the prune would
  // actually delete a prefix, then truncate. Serialization + the
  // "prune is non-empty" gate together make the archive fire at most
  // once per pruned range when a manual `app.close` races an autoclose
  // windowed close for the same stream — the second closer, run behind
  // the lock, sees the already-pruned prefix (boundary is now the
  // earliest event) and skips its archive. Each stream is independent,
  // so `truncate` is called per stream inside its own lock rather than
  // once for the batch; a windowed truncate touches only its own stream.
  const with_lock = deps.with_stream_lock ?? ((_stream, work) => work());
  const truncated: CloseResult["truncated"] = new Map();
  for (const t of windowed) {
    const entry = await with_lock(t.stream, async () => {
      const max_id = min_at.get(t.stream);
      // Boundary probe: will a windowed truncate prune anything? The
      // store deletes events with `id < boundary.id`, where `boundary`
      // is the latest `__snapshot__` with `created < before` (and, when
      // capped, `id <= max_id`). No such prefix ⇒ archive is skipped and
      // the stream is reported skipped, exactly as a no-op truncate would.
      if (!(await windowed_prune_pending(t.stream, t.before!, max_id)))
        return undefined;
      // Archive: user callback against the cutoff, run while the prefix
      // is still present. Sequential/fail-fast: a throw propagates to the
      // caller and leaves the stream un-truncated (no data loss).
      if (t.archive) await t.archive();
      // Boundary truncate: atomic per-store transaction; no seed.
      const result = await store().truncate([
        { stream: t.stream, before: t.before!, max_id },
      ]);
      return result.get(t.stream);
    });
    if (entry) truncated.set(t.stream, entry);
    else skipped.push(t.stream);
  }
  return truncated;
}

/**
 * Read-only probe: would a windowed truncate of `stream` at `before`
 * (optionally capped at `max_id`) delete a prefix? Mirrors the store's
 * boundary rule — the latest `__snapshot__` with `created < before` and,
 * when capped, `id <= max_id` — then reports whether any event sorts
 * strictly below that boundary. False when no snapshot qualifies (a
 * no-op truncate) or when the boundary is already the earliest event
 * (the prefix was pruned by a prior closer). This is the guard that
 * makes the windowed archive fire at most once per pruned range (#1222).
 *
 * @internal
 */
async function windowed_prune_pending(
  stream: string,
  before: Date,
  max_id: number | undefined
): Promise<boolean> {
  let boundary_id: number | undefined;
  let min_id: number | undefined;
  await store().query(
    (event) => {
      if (min_id === undefined || event.id < min_id) min_id = event.id;
      if (
        event.name === SNAP_EVENT &&
        event.created < before &&
        (max_id === undefined || event.id <= max_id) &&
        (boundary_id === undefined || event.id > boundary_id)
      )
        boundary_id = event.id;
    },
    { stream, stream_exact: true, with_snaps: true, after: -1 }
  );
  // No qualifying snapshot → nothing to prune behind. Otherwise a prune
  // is pending only when some event sorts below the boundary.
  return boundary_id !== undefined && min_id! < boundary_id;
}

/**
 * Min subscription watermark per target stream — the read-only probe
 * backing the windowed close's `max_id` cap. Pagination and source
 * matching mirror {@link partition_by_safety}; instead of flagging
 * pending streams it folds `min(at)` per stream. Streams with no
 * matching subscriptions are absent (no cap).
 *
 * @internal
 */
async function probe_min_watermarks(
  streams: string[],
  page_size: number,
  checkpoint: number
): Promise<Map<string, number>> {
  const min_at = new Map<string, number>();
  const source_regex = new Map<string, RegExp>();
  const get_regex = (source: string): RegExp => {
    let re = source_regex.get(source);
    if (!re) {
      re = new RegExp(source);
      source_regex.set(source, re);
    }
    return re;
  };

  let after: string | undefined;
  for (;;) {
    let last: string | undefined;
    const { count } = await store().query_streams(
      (position) => {
        last = position.stream;
        const source_re = position.source
          ? get_regex(position.source)
          : undefined;
        // How far this consumer permits a prune. A row with unconsumed work
        // caps at its watermark, as it always did. A row that has consumed
        // everything marked for it caps at the correlate checkpoint instead —
        // its watermark says nothing about safety, only about which event
        // types it happens to handle. An unmarked row keeps the conservative
        // watermark cap, matching how it is read everywhere else until every
        // install has converted.
        const pending =
          position.correlated_at === undefined ||
          position.at < position.correlated_at;
        const cap = pending ? position.at : Math.max(position.at, checkpoint);
        for (const stream of streams) {
          if (!source_re || source_re.test(stream)) {
            const prev = min_at.get(stream);
            if (prev === undefined || cap < prev) min_at.set(stream, cap);
          }
        }
      },
      { after, limit: page_size, source_matches: streams }
    );
    if (count < page_size) break;
    after = last;
  }
  return min_at;
}

// ---------------------------------------------------------------------------
// Phase 1 — scan stream heads
// ---------------------------------------------------------------------------

async function scan_stream_heads(
  streams: string[]
): Promise<Map<string, StreamHead>> {
  // query_stats returns the latest non-snap event per stream (heads-only
  // cheap path, indexed). Streams whose latest non-snap event is a tombstone
  // are filtered out in the loop — we don't want to re-tombstone an
  // already-closed stream. Streams with no events (or only snap/tombstone
  // events filtered out) are absent from the result map entirely.
  const stats = await store().query_stats(streams, {
    exclude: [SNAP_EVENT],
  });
  // Domain head, markers excluded. A stream absent here has no domain
  // events left, so a previous close ran to completion and there is
  // nothing to do. A stream present here whose `stats` head is a tombstone
  // was guarded and then interrupted — it must be resumed, not skipped
  // (#1389). `last_event_name` also has to come from this pass: the
  // restart-seed owner lookup needs the domain event, not the marker.
  const domain_heads = await store().query_stats(streams, {
    exclude: [SNAP_EVENT, TOMBSTONE_EVENT],
  });
  // The tombstone's optimistic lock must expect the stream's ACTUAL current
  // version, which is one higher when a `__snapshot__` trails the domain head
  // — `snap()` commits it into the next version slot (event-sourcing.ts). The
  // domain head above still drives `max_id` (the safety probe: a subscription
  // advances its watermark on domain events, never snapshots) and
  // `last_event_name` (the restart-seed owner lookup). Only the guard version
  // needs the true head, so a second heads-only pass reads it with snapshots
  // included. Without this, a terminal commit that crossed a `.snap()`
  // boundary makes the guard expect a stale version and skip the close (#1356).
  const true_heads = await store().query_stats(streams, {});
  const out = new Map<string, StreamHead>();
  for (const [stream, { head }] of domain_heads) {
    const marker_head = stats.get(stream)?.head;
    const interrupted = marker_head?.name === TOMBSTONE_EVENT;
    out.set(stream, {
      max_id: head.id,
      version: true_heads.get(stream)!.head.version,
      last_event_name: head.name as string,
      ...(interrupted ? { resumed_guard: { id: marker_head.id } } : {}),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Phase 2 — partition by safety
// ---------------------------------------------------------------------------

async function partition_by_safety(
  stream_info: Map<string, StreamHead>,
  reactive_events_size: number,
  skipped: string[],
  page_size: number,
  catch_up_correlation: (until: number) => Promise<number>
): Promise<string[]> {
  if (reactive_events_size === 0) return [...stream_info.keys()];

  // Correlate the tail first (#1487). A head past the read cursor has
  // raised no mark, so the probe below would read every reader as caught
  // up on it — and the reader that needs it may not even be subscribed
  // yet. Catching up both raises the marks and creates those subscriptions,
  // so the probe answers about the real state of the log. Anything still
  // above the cursor afterwards (the log outran us) is held back.
  let needed = -1;
  for (const info of stream_info.values())
    needed = Math.max(needed, info.max_id);
  const checkpoint = await catch_up_correlation(needed);
  const uncorrelated = new Set<string>();
  for (const [stream, info] of stream_info) {
    if (checkpoint < info.max_id) uncorrelated.add(stream);
  }

  // Read-only probe: query_streams returns subscription positions without
  // leasing or mutating retry state.
  //
  // The stored `source` on a subscription may be a literal stream name or
  // a pattern (e.g. `^(A|B)$`); this probe matches it as a regex against
  // close-target names either way — a literal regex-matches itself, and a
  // pattern matches the streams it claims for. Any metacharacter
  // over-match only widens the pending set — the conservative direction
  // for a safety probe. Compiled patterns are cached because dynamic
  // reactions commonly produce many subscriptions sharing one source, so
  // the callback fires repeatedly with the same `source`.
  const pending_set = new Set<string>();
  const source_regex = new Map<string, RegExp>();
  const get_regex = (source: string): RegExp => {
    let re = source_regex.get(source);
    if (!re) {
      re = new RegExp(source);
      source_regex.set(source, re);
    }
    return re;
  };

  // `source_matches` narrows the probe server-side to subscriptions that
  // could consume from a stream we're closing — a best-effort hint, so
  // the per-position source/target re-check below still runs and keeps
  // the result correct even when a store returns a superset.
  const targets = [...stream_info.keys()];

  // Keyset-paginate the (narrowed) subscriptions on the `after` cursor —
  // `query_streams` caps each call at `limit` rows, so every page is
  // inspected until a short page signals the last one. A lagging reaction
  // marks its close target pending regardless of how far its subscription
  // sorts past the first page.
  let after: string | undefined;
  for (;;) {
    let last: string | undefined;
    const { count } = await store().query_streams(
      (position) => {
        last = position.stream;
        const source_re = position.source
          ? get_regex(position.source)
          : undefined;
        // "Behind the head" stopped meaning "has work to do" when correlate
        // became the producer of the work mark (#1487): a subscription's
        // watermark advances only over events that resolve to it, so a
        // consumer of two of a state's ten event types sits permanently below
        // a head it has no reaction for. Asking the row the same question
        // `claim` asks keeps the guard honest — and keeps close from skipping
        // every such stream forever.
        //
        // An unmarked row is not pending, and that is now definitional rather
        // than conservative (#1488): `claim` will never serve it either, so
        // there is no consumer to wait for. The catch-up above is what makes
        // the reading safe — any mark that was owed has landed by here.
        const has_work =
          position.correlated_at !== undefined &&
          position.at < position.correlated_at;
        if (!has_work) return;
        for (const [stream, info] of stream_info) {
          if (
            (!source_re || source_re.test(stream)) &&
            position.at < info.max_id
          ) {
            pending_set.add(stream);
          }
        }
      },
      { after, limit: page_size, source_matches: targets }
    );
    if (count < page_size) break;
    after = last;
  }

  const safe: string[] = [];
  for (const [stream] of stream_info) {
    if (pending_set.has(stream) || uncorrelated.has(stream))
      skipped.push(stream);
    else safe.push(stream);
  }
  return safe;
}

// ---------------------------------------------------------------------------
// Phase 3 — guard with tombstones
// ---------------------------------------------------------------------------

async function guard_with_tombstones(
  safe: string[],
  stream_info: Map<string, StreamHead>,
  correlation: string,
  tombstone: EsOps["tombstone"],
  skipped: string[]
): Promise<{
  guarded: string[];
  guard_events: Map<string, { id: number; stream: string }>;
}> {
  const guarded: string[] = [];
  const guard_events = new Map<string, { id: number; stream: string }>();
  await Promise.all(
    safe.map(async (stream) => {
      const info = stream_info.get(stream)!;
      if (info.resumed_guard) {
        // Guard already written by the interrupted run — reuse it rather
        // than re-tombstoning (the version guard would reject it anyway).
        guarded.push(stream);
        guard_events.set(stream, { id: info.resumed_guard.id, stream });
        return;
      }
      const committed = await tombstone(stream, info.version, correlation);
      if (committed) {
        guarded.push(stream);
        guard_events.set(stream, { id: committed.id, stream });
      } else {
        // ConcurrencyError → another writer beat the guard
        skipped.push(stream);
      }
    })
  );
  return { guarded, guard_events };
}

// ---------------------------------------------------------------------------
// Phase 4 — load restart seeds
// ---------------------------------------------------------------------------

async function load_restart_seeds(
  guarded: string[],
  target_map: Map<string, CloseTarget>,
  stream_info: Map<string, StreamHead>,
  event_to_state: ReadonlyMap<string, State<any, any, any>>,
  load: EsOps["load"],
  logger: Logger
): Promise<Map<string, Schema>> {
  const seed_states = new Map<string, Schema>();
  await Promise.all(
    guarded
      .filter((s) => target_map.get(s)?.restart)
      .map(async (stream) => {
        // stream_info entry is guaranteed (guarded ⊆ stream_info.keys()).
        const last_event_name = stream_info.get(stream)!.last_event_name;
        const owner_state = event_to_state.get(last_event_name);
        if (!owner_state) {
          // No registered state owns the stream's events (deleted state,
          // schema versioning gone wrong, etc.). Tombstone instead of
          // seeding a corrupted snapshot.
          logger.error(
            `Cannot seed restart for "${stream}": no registered state owns event "${last_event_name}". Stream will be tombstoned instead.`
          );
          return;
        }
        const snap = await load(owner_state, { stream });
        seed_states.set(stream, snap.state as Schema);
      })
  );
  return seed_states;
}

// ---------------------------------------------------------------------------
// Phase 5 — archive callbacks
// ---------------------------------------------------------------------------

async function run_archive_callbacks(
  guarded: string[],
  target_map: Map<string, CloseTarget>
): Promise<void> {
  // Sequential — user callbacks may share resources (S3 client, etc.) and
  // a failure should propagate to the caller without leaving partial state.
  for (const stream of guarded) {
    const archive_fn = target_map.get(stream)?.archive;
    if (archive_fn) await archive_fn();
  }
}

// ---------------------------------------------------------------------------
// Phase 6 — atomic truncate + cache warm
// ---------------------------------------------------------------------------

async function truncate_and_warm_cache(
  guarded: string[],
  seed_states: Map<string, Schema>,
  guard_events: Map<string, { id: number; stream: string }>,
  correlation: string
): Promise<CloseResult["truncated"]> {
  const trunc_targets = guarded.map((stream) => {
    const snapshot = seed_states.get(stream);
    const guard = guard_events.get(stream)!;
    return {
      stream,
      snapshot,
      meta: {
        correlation,
        causation: {
          event: { id: guard.id, name: TOMBSTONE_EVENT, stream: guard.stream },
        },
      },
    };
  });
  const truncated = await store().truncate(trunc_targets);

  // Cache invalidate / warm — use real event IDs from committed events
  await Promise.all(
    guarded.map(async (stream) => {
      const entry = truncated.get(stream);
      const state = seed_states.get(stream);
      if (state && entry) {
        await cache().set(stream, {
          stream,
          state,
          version: entry.committed.version,
          event_id: entry.committed.id,
          patches: 0,
          snaps: 1,
        });
      } else {
        await cache().invalidate(stream);
      }
    })
  );

  return truncated;
}
